import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Stethoscope, DotsThreeVertical, PencilSimple, Trash, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import DoctorForm from '@/components/DoctorForm';
import { DoctorContact } from '@/components/DoctorBits';
import { DoctorName } from '@/components/Present';
import { useAuth } from '@/context/AuthContext';

function usageLabel(u) {
    if (!u || !u.in_use) return 'Not linked to anyone yet';
    const bits = [];
    if (u.member_count) bits.push(`${u.member_count} family member${u.member_count === 1 ? '' : 's'}`);
    if (u.appointment_count) bits.push(`${u.appointment_count} appointment${u.appointment_count === 1 ? '' : 's'}`);
    if (u.medication_count) bits.push(`${u.medication_count} medication${u.medication_count === 1 ? '' : 's'}`);
    return `Linked to ${bits.join(', ')}`;
}

export default function Doctors() {
    const qc = useQueryClient();
    const { isDemoAdmin } = useAuth();
    const [q, setQ] = useState('');
    const [dialog, setDialog] = useState({ open: false, initial: null });
    const [del, setDel] = useState(null);
    const { data: doctors = [], isLoading } = useQuery({
        queryKey: ['doctors', 'directory'],
        queryFn: async () => (await api.get('/doctors')).data,
    });
    const needle = q.trim().toLowerCase();
    const list = doctors.filter(d => !needle || (d.name || '').toLowerCase().includes(needle) || (d.specialty || '').toLowerCase().includes(needle));
    const refresh = () => qc.invalidateQueries({ queryKey: ['doctors'] });
    const doDelete = async () => {
        try {
            await api.delete(`/doctors/${del.provider_id}`);
            toast.success('Doctor deleted'); setDel(null); refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed');
        }
    };

    return (
        <div data-testid="doctors-root" className="px-4 sm:px-6 md:px-10 py-6 md:py-8 max-w-5xl">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Directory</div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 mt-1">Doctors</h1>
                    <div className="text-sm text-slate-600 mt-2" data-testid="doctors-count">{doctors.length} doctor{doctors.length === 1 ? '' : 's'} across your households</div>
                </div>
                {!isDemoAdmin && (
                    <Button data-testid="doctors-add" className="rounded-full bg-primary text-primary-foreground min-h-[44px]" onClick={() => setDialog({ open: true, initial: null })}>
                        <Plus size={16} className="mr-1" /> Add doctor
                    </Button>
                )}
            </div>

            <div className="relative mb-4">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="doctors-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or specialty" className="pl-9 min-h-[44px]" />
            </div>

            <div className="rounded-xl border border-slate-200 bg-card shadow-sm divide-y divide-slate-200">
                {isLoading && <div className="px-5 py-10 text-center text-sm text-slate-500">Loading…</div>}
                {!isLoading && list.length === 0 && (
                    <div className="px-5 py-12 text-center text-sm text-slate-500" data-testid="doctors-empty">
                        <Stethoscope size={28} className="mx-auto text-slate-400 mb-2" />
                        {doctors.length === 0 ? 'No doctors yet. Add one here, or from any family member’s “Their doctors” tab.' : 'No doctors match your search.'}
                    </div>
                )}
                {list.map(d => (
                    <div key={d.provider_id} data-testid="doctors-row" className="px-4 sm:px-5 py-4 flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="text-base font-medium text-slate-900" data-testid="doctors-row-name"><DoctorName name={d.name} /></div>
                            <div className="text-xs text-slate-500 mt-0.5">{d.specialty || 'Specialty not set'}</div>
                            <div className="mt-2"><DoctorContact doctor={d} compact testidPrefix="doctors-row" /></div>
                            <div className="mt-2 text-[11px] text-slate-500" data-testid="doctors-row-usage">
                                {usageLabel(d.usage)}
                                {d.usage?.household_count > 1 && ` · used by ${d.usage.household_count} households`}
                            </div>
                        </div>
                        {!isDemoAdmin && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button data-testid="doctors-row-menu" className="p-2 rounded hover:bg-slate-100 text-slate-500 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:p-1.5 flex items-center justify-center" aria-label="More"><DotsThreeVertical size={18} weight="bold" /></button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem data-testid="doctors-row-edit" onClick={() => setDialog({ open: true, initial: d })}><PencilSimple size={14} className="mr-2" /> Edit</DropdownMenuItem>
                                    <DropdownMenuItem data-testid="doctors-row-delete" onClick={() => setDel(d)} className="text-red-600 focus:text-red-700"><Trash size={14} className="mr-2" /> Delete</DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                ))}
            </div>

            <Dialog open={dialog.open} onOpenChange={(v) => setDialog({ open: v, initial: v ? dialog.initial : null })}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{dialog.initial ? 'Edit doctor' : 'Add doctor'}</DialogTitle><DialogDescription>Name, specialty and how to reach the office.</DialogDescription></DialogHeader>
                    <DoctorForm initial={dialog.initial} onSaved={() => { setDialog({ open: false, initial: null }); refresh(); }} />
                </DialogContent>
            </Dialog>

            {del?.usage?.in_use ? (
                <ConfirmDialog
                    open={!!del} onOpenChange={(v) => !v && setDel(null)}
                    title={`${del.name} is still linked`}
                    description={`${usageLabel(del.usage)}. Remove them from each family member (“Remove from this member”), appointment and medication first — then you can delete.`}
                    confirmLabel="OK"
                    destructive={false}
                    onConfirm={() => setDel(null)}
                    testId="doctors-delete-blocked"
                />
            ) : (
                <ConfirmDialog
                    open={!!del} onOpenChange={(v) => !v && setDel(null)}
                    title={`Delete ${del?.name || 'this doctor'}?`}
                    description="They aren't linked to anyone. This removes them from your doctors list and can't be undone."
                    onConfirm={doDelete}
                    testId="doctors-delete-confirm"
                />
            )}
        </div>
    );
}
